/**
 * Daily Set Generator Module
 * Handles generation and management of daily practice sets
 */

import logger from '../../core/logger.js';
import { EVENT_TYPES } from '../../core/event-types.js';
import { logEvent } from '../../core/event-logger.js';
import appState from '../../core/app-state.js';
import { appFlags } from '../../config/feature-flags.js';
import type { Clip } from '../../types/clip.js';

/**
 * Get today's date in ISO format (YYYY-MM-DD)
 */
function getTodayDate(): string {
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  return dateStr ?? '';
}

/**
 * Load stored daily set for a specific date and level
 */
function loadDailySetForDate(date: string, level: string): string[] | null {
  if (!appFlags.dailySetPersistsByDate) {
    return null;
  }
  
  try {
    const key = `dailySet:${date}:${level}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored) as string[];
    }
  } catch (e) {
    logger.warn('Failed to load daily set for date', e);
  }
  return null;
}

/**
 * Save daily set for a specific date and level
 */
function saveDailySetForDate(date: string, level: string, clipIds: string[]): void {
  if (!appFlags.dailySetPersistsByDate) {
    return;
  }
  
  try {
    const key = `dailySet:${date}:${level}`;
    localStorage.setItem(key, JSON.stringify(clipIds));
  } catch (e) {
    logger.warn('Failed to save daily set for date', e);
  }
}

/**
 * Get primary topic for a clip (uses storyId or lessonId as topic indicator)
 */
function getPrimaryTopic(clip: Clip): string | number {
  return clip.storyId || clip.lessonId || 'general';
}

/**
 * Select clips with topic variety constraint
 */
function selectWithTopicVariety(availableClips: Clip[], count: number, alreadySelected: Clip[]): Clip[] {
  if (availableClips.length === 0 || count === 0) {
    return [];
  }
  
  // Track topics already selected
  const usedTopics = new Set(alreadySelected.map(c => getPrimaryTopic(c)));
  const selected: Clip[] = [];
  const remaining = [...availableClips];
  
  // Shuffle for randomness using Fisher-Yates algorithm
  for (let i = remaining.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = remaining[i];
    const tempJ = remaining[j];
    if (temp && tempJ) {
      remaining[i] = tempJ;
      remaining[j] = temp;
    }
  }
  
  // First pass: select clips from different topics
  for (const clip of remaining) {
    if (selected.length >= count) break;
    
    const topic = getPrimaryTopic(clip);
    if (!usedTopics.has(topic)) {
      selected.push(clip);
      usedTopics.add(topic);
    }
  }
  
  // Second pass: if we still need more clips, add any remaining
  if (selected.length < count) {
    const selectedIds = new Set(selected.map(c => c.id));
    for (const clip of remaining) {
      if (selected.length >= count) break;
      if (!selectedIds.has(clip.id)) {
        selected.push(clip);
      }
    }
  }
  
  return selected;
}

/**
 * Find a good series to start (part 1 of a series)
 */
function findNewSeriesStart(availableClips: Clip[], alreadySelected: Clip[]): Clip | null {
  const selectedIds = new Set(alreadySelected.map(c => c.id));
  
  // Find clips that are part 1 of a series
  for (const clip of availableClips) {
    if (clip.storyId && clip.storyPart === 1 && !selectedIds.has(clip.id)) {
      // Check if this series hasn't been started
      const hasCompletedInSeries = appState.clips.some(c => 
        c.storyId === clip.storyId && isClipCompleted(c.id)
      );
      
      if (!hasCompletedInSeries) {
        return clip;
      }
    }
  }
  
  return null;
}

/**
 * Find continuity clips (continuity-first selection with 2 concurrent series cap)
 */
function findContinuityClips(availableClips: Clip[], maxClips: number): Clip[] {
  // Find all series where user has started but not completed
  const activeSeries = new Map<number, { latestCompletedPart: number; nextClip: Clip }>(); // storyId -> { latestCompletedPart, nextClip }
  
  // Scan for active series (where user has completed at least one part)
  for (const clip of availableClips) {
    if (clip.storyId && clip.storyPart !== undefined) {
      const currentPart = clip.storyPart;
      // Check if there's a completed previous part
      const prevPartClip = appState.clips.find(c => 
        c.storyId === clip.storyId && 
        c.storyPart !== undefined &&
        c.storyPart === (currentPart - 1)
      );
      
      if (prevPartClip && isClipCompleted(prevPartClip.id) && !isClipCompleted(clip.id)) {
        // This is a valid continuation
        const existing = activeSeries.get(clip.storyId);
        if (!existing || existing.latestCompletedPart < currentPart - 1) {
          activeSeries.set(clip.storyId, {
            latestCompletedPart: currentPart - 1,
            nextClip: clip
          });
        }
      }
    }
  }
  
  // Select up to 2 active series (cap concurrent series)
  const continuityClips = [];
  let seriesCount = 0;
  const maxConcurrentSeries = 2;
  
  for (const [_, seriesInfo] of activeSeries) {
    if (seriesCount >= maxConcurrentSeries) break;
    if (continuityClips.length >= maxClips) break;
    
    continuityClips.push(seriesInfo.nextClip);
    seriesCount++;
  }
  
  // If we have room and less than 2 active series, consider starting one new series
  if (continuityClips.length < maxClips && seriesCount < maxConcurrentSeries) {
    const newSeriesClip = findNewSeriesStart(availableClips, continuityClips);
    if (newSeriesClip) {
      continuityClips.push(newSeriesClip);
    }
  }
  
  return continuityClips;
}

/**
 * Find series continuity clip (legacy helper)
 */
export function findSeriesContinuityClip(availableClips: Clip[]): Clip | null {
  // Find clips where user has completed a previous part in the same series
  for (const clip of availableClips) {
    if (clip.storyId && clip.storyPart !== undefined && clip.storyPart > 1) {
      const currentPart = clip.storyPart;
      // Check if previous part is completed
      const prevPartClip = appState.clips.find(c => 
        c.storyId === clip.storyId && 
        c.storyPart !== undefined &&
        c.storyPart === (currentPart - 1)
      );
      
      if (prevPartClip && isClipCompleted(prevPartClip.id)) {
        return clip;
      }
    }
  }
  
  return null;
}

/**
 * Check if a clip is completed
 */
function isClipCompleted(clipId: string): boolean {
  return !!appState.progressMap[clipId];
}

/**
 * Generate daily set of clips based on user preferences
 */
export function generateDailySet(source: 'auto' | 'manualShuffle' | 'onboarding' = 'auto'): void {
  if (!appState.clips || appState.clips.length === 0) {
    appState.dailySet = [];
    return;
  }
  
  const today = getTodayDate();
  const userLevel = (appState.preferences['cefrLevel'] as string | undefined) || 'B2';
  
  // Try to load existing set for today if persistence enabled
  if (appFlags.dailySetPersistsByDate && source === 'auto') {
    const stored = loadDailySetForDate(today, userLevel);
    if (stored && stored.length > 0) {
      appState.dailySet = stored;
      
      // Log resumed event if there are incomplete clips in the set
      const completedCount = stored.filter(id => isClipCompleted(id)).length;
      if (completedCount < stored.length) {
        const eventType = EVENT_TYPES['DAILY_GOAL_RESUMED'];
        if (eventType) {
          logEvent(eventType, {
            date: today,
            completedCount: completedCount,
            totalCount: stored.length
          });
        }
      }
      return;
    }
  }
  
  const setSize = (appState.preferences['dailyGoal'] as number | undefined) || 1;
  const enableContinuity = appFlags.enableContinuitySlot;
  const learningLang = (appState.preferences['learningLanguage'] as string | undefined) || 'tr';
  
  // Filter clips by learning language first (unless "all" is selected)
  let languageFilteredClips = appState.clips;
  if (learningLang && learningLang !== 'all') {
    languageFilteredClips = appState.clips.filter(c => c.language === learningLang);
  }
  
  // Filter clips by CEFR level with adjacent-level backfill
  let levelFilteredClips = languageFilteredClips.filter(c => 
    c.difficulty && c.difficulty.toLowerCase() === userLevel.toLowerCase()
  );
  
  let backfillUsed = false;
  let backfillCount = 0;
  let backfillLevel: string | null = null;
  
  // If not enough clips at target level, backfill from adjacent levels (cascading)
  if (levelFilteredClips.length < setSize) {
    backfillUsed = true;
    
    // First attempt: backfill from adjacent level
    const adjacentLevel = userLevel === 'B2' ? 'B1' : (userLevel === 'B1' ? 'A2' : null);
    
    if (adjacentLevel) {
      const adjacentClips = languageFilteredClips.filter(c => 
        c.difficulty && c.difficulty.toLowerCase() === adjacentLevel.toLowerCase()
      );
      const needed = setSize - levelFilteredClips.length;
      backfillCount = Math.min(needed, adjacentClips.length);
      backfillLevel = adjacentLevel;
      levelFilteredClips = levelFilteredClips.concat(adjacentClips.slice(0, backfillCount));
    }
    
    // If still not enough, cascade to next level (B2 -> B1 -> A2)
    if (levelFilteredClips.length < setSize && userLevel === 'B2' && adjacentLevel === 'B1') {
      const secondaryLevel = 'A2';
      const secondaryClips = languageFilteredClips.filter(c => 
        c.difficulty && c.difficulty.toLowerCase() === secondaryLevel.toLowerCase()
      );
      // Exclude clips already added
      const alreadyAdded = new Set(levelFilteredClips.map(c => c.id));
      const newSecondaryClips = secondaryClips.filter(c => !alreadyAdded.has(c.id));
      
      const stillNeeded = setSize - levelFilteredClips.length;
      const secondaryCount = Math.min(stillNeeded, newSecondaryClips.length);
      
      if (secondaryCount > 0) {
        levelFilteredClips = levelFilteredClips.concat(newSecondaryClips.slice(0, secondaryCount));
        backfillCount += secondaryCount;
        backfillLevel = `${adjacentLevel}+${secondaryLevel}`; // Indicate multiple levels used
      }
    }
  }
  
  // Get incomplete clips (not yet completed)
  const incompleteClips = levelFilteredClips.filter(clip => !isClipCompleted(clip.id));
  
  // If no incomplete clips, use all level-filtered clips
  const availableClips = incompleteClips.length > 0 ? incompleteClips : levelFilteredClips;
  
  let selectedClips: string[] = [];
  let continuityUsed = false;
  
  if (enableContinuity && availableClips.length > 0) {
    // Continuity-first selection: continue in-progress series (cap 2 concurrent)
    const continuityClips = findContinuityClips(availableClips, setSize);
    
    if (continuityClips.length > 0) {
      selectedClips.push(...continuityClips.map(c => c.id));
      continuityUsed = true;
      
      // Fill remaining slots with non-continuity clips, applying topic variety
      const remainingSlots = setSize - selectedClips.length;
      if (remainingSlots > 0) {
        const remainingClips = availableClips.filter(c => !selectedClips.includes(c.id));
        const selectedWithVariety = selectWithTopicVariety(remainingClips, remainingSlots, continuityClips);
        selectedClips.push(...selectedWithVariety.map(c => c.id));
      }
    } else {
      // No continuity clips, pick clips with topic variety
      const selectedWithVariety = selectWithTopicVariety(availableClips, setSize, []);
      selectedClips = selectedWithVariety.map(c => c.id);
    }
  } else {
    // No continuity slot, pick clips with topic variety
    const selectedWithVariety = selectWithTopicVariety(availableClips, setSize, []);
    selectedClips = selectedWithVariety.map(c => c.id);
  }
  
  appState.dailySet = selectedClips;
  
  // Debug logging (only if dev mode enabled)
  logger.debug('generateDailySet', {
    requestedSize: setSize,
    actualSize: selectedClips.length,
    availableClipsCount: availableClips.length,
    levelFilteredCount: levelFilteredClips.length,
    incompleteCount: incompleteClips.length,
    continuityUsed: continuityUsed,
    backfillUsed: backfillUsed,
    selectedClipIds: selectedClips
  });
  
  // Save to localStorage if persistence enabled
  if (appFlags.dailySetPersistsByDate) {
    saveDailySetForDate(today, userLevel, selectedClips);
  }
  
  // Log daily set generation with enhanced fields
  if (selectedClips.length > 0) {
    const eventSource = source === 'manualShuffle' ? 'manualShuffle' : 
                       (source === 'onboarding' ? 'onboarding' : 'rollover');
    
    const generatedEventType = EVENT_TYPES['DAILY_GOAL_GENERATED'];
    if (generatedEventType) {
      logEvent(generatedEventType, {
        date: today,
        level: userLevel,
        size: selectedClips.length,
        continuityUsed: continuityUsed,
        topicVarietyApplied: true,
        source: eventSource,
        backfillUsed: backfillUsed,
        backfillCount: backfillCount,
        backfillLevel: backfillLevel
      });
    }
    
    // Also log the SHOWN event for compatibility
    const shownEventType = EVENT_TYPES['DAILY_GOAL_SHOWN'];
    if (shownEventType) {
      logEvent(shownEventType, {
        clipCount: selectedClips.length,
        clipIds: selectedClips
      });
    }
  }
}

/**
 * Daily Set Generator class for backward compatibility
 */
export class DailySetGenerator {
  /**
   * Generate a daily set
   */
  generate(source: 'auto' | 'manualShuffle' | 'onboarding' = 'auto'): void {
    generateDailySet(source);
  }
}

// Export singleton instance
export const dailySetGenerator = new DailySetGenerator();

// Export utility functions
export { getTodayDate, loadDailySetForDate, saveDailySetForDate };
